/**
 * Login validation — only requires a valid ProfileID.
 * ActiveStatus = N users may log in; browse restriction is handled in viewerAccess.js.
 */

import { pickField, syncViewerAccessFlags } from "./viewerAccess";

export class LoginNotAllowedError extends Error {
  constructor(code, message) {
    super(message);
    this.name = "LoginNotAllowedError";
    this.code = code;
  }
}

/** user_login may return data as an object or single-row array. */
export function unwrapLoginRow(apiResponse) {
  const raw = apiResponse?.data;
  if (Array.isArray(raw)) return raw[0] ?? null;
  return raw ?? null;
}

/**
 * @param {object} apiResponse — full JSON from user_login
 * @returns {object} data row when allowed
 */
export function assertCandidateMayLogin(apiResponse) {
  const row = unwrapLoginRow(apiResponse);

  if (!row || typeof row !== "object" || Array.isArray(row)) {
    throw new LoginNotAllowedError(
      "INVALID",
      "Invalid login response from server. Please try again.",
    );
  }

  const profileId = pickField(row, ["ProfileID", "profileID", "profile_id"]);
  if (profileId == null || profileId === "" || String(profileId) === "0") {
    throw new LoginNotAllowedError(
      "NO_PROFILE",
      "Your profile could not be found for login. Please contact the admin (+91 98068 63659).",
    );
  }

  return row;
}

export function clearLoginSession() {
  [
    "profileID",
    "userID",
    "profileName",
    "profilePhoto",
    "login_token",
    "viewerGenderID",
    "viewerActiveStatus",
    "viewerBrowseRestricted",
  ].forEach((key) => localStorage.removeItem(key));
}

/** Save session after login; stores ActiveStatus for browse rules. */
export function persistLoginSession(apiResponse) {
  const row = unwrapLoginRow(apiResponse);
  if (!row) return;

  const profileId = pickField(row, ["ProfileID", "profileID", "profile_id"]);
  if (profileId != null) {
    localStorage.setItem("profileID", String(profileId));
  }

  const userId = pickField(row, ["UserID", "userID", "user_id"]);
  if (userId != null) localStorage.setItem("userID", String(userId));

  const profileName = pickField(row, [
    "ProfileName",
    "profileName",
    "profile_name",
    "firstname",
  ]);
  if (profileName != null) {
    localStorage.setItem("profileName", String(profileName));
  }

  if (apiResponse.login_token) {
    localStorage.setItem("login_token", apiResponse.login_token);
  } else {
    localStorage.removeItem("login_token");
  }
  syncViewerAccessFlags(row);
}
